import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, Params } from '@angular/router';
import { ITopbarHeader } from '@core/interface/topbar.interface';
import { TOPBAR_HEADER } from '@shared/utils/constants/common.constant';
import { BehaviorSubject, filter, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TopbarService {
  private topbarHeaderSubject = new BehaviorSubject<ITopbarHeader>({
    title: '',
    breadCrumbs: [],
    backRoute: '',
  });
  private backRouteSubject = new BehaviorSubject<string>('');
  public topbarHeader$ = this.topbarHeaderSubject.asObservable();
  public backRoute$ = this.backRouteSubject.asObservable();

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this.initializeRouterListener();
  }

  /**
   * Initializes the router event listener to update topbar header and back route.
   */
  private initializeRouterListener(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap(() => {
          const { topbarHeader, params } = this.extractTopbarConfig(
            this.activatedRoute,
          );
          this.topbarHeaderSubject.next(topbarHeader);
          const builtBackRoute = this.generateBackRoute(
            topbarHeader.backRoute,
            params,
          );
          this.backRouteSubject.next(builtBackRoute);
        }),
      )
      .subscribe();
  }

  /**
   * Extracts the configuration for the current route.
   *
   * @param route - The currently activated route.
   * @returns The extracted topbar header configuration and route parameters.
   */
  private extractTopbarConfig(route: ActivatedRoute): {
    topbarHeader: ITopbarHeader;
    params: Params;
  } {
    let currentRoute = route;
    let accumulatedParams: Params = { ...route.snapshot.params };

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      accumulatedParams = {
        ...accumulatedParams,
        ...currentRoute.snapshot.params,
      };
    }

    const topbarHeader: ITopbarHeader = currentRoute.snapshot.data[
      TOPBAR_HEADER
    ] || {
      route: '',
      title: '',
      breadCrumbs: [],
      backRoute: '',
    };

    return { topbarHeader, params: accumulatedParams };
  }

  /**
   * Generates the back route using a base route string and route parameters.
   *
   * @param baseBackRoute - The base back route configuration string.
   * @param params - The current route parameters.
   * @returns The constructed back route with parameters.
   */
  private generateBackRoute(
    baseBackRoute: string | undefined,
    params: Params,
  ): string {
    if (!baseBackRoute) return '';

    const backRouteWithParams = baseBackRoute
      .split('/')
      .map((segment) => {
        if (segment.startsWith(':')) {
          const paramName = segment.substring(1);
          const paramValue = params[paramName];
          return paramValue ? paramValue : segment;
        }
        return segment;
      })
      .join('/');

    return backRouteWithParams;
  }
}
